// The configurator as the customer and the public visitor meet it. The staff
// tool that authors this tree is CONFIGURATOR_CMS.
import { ANCILLARY_COST_RATE } from '../../constants/pricing'

import { COMMON } from './common'
import { COST_TERMS } from './costs'
import { CONFIGURATOR_STATUS_LABELS } from './enum-labels'
import { TERMS } from './terms'

export const CONFIGURATOR = {
  /** The dialog in front of a pick that clears another one. */
  deselect: {
    /** The press chooses something, and the catalogue clears other measures to
     *  make room for it. */
    replace: {
      title: 'Auswahl ersetzt eine andere',
      body: (n: number) =>
        n === 1
          ? 'Mit dieser Auswahl entfällt die folgende Maßnahme:'
          : 'Mit dieser Auswahl entfallen die folgenden Maßnahmen:',
      confirm: 'Trotzdem auswählen',
    },
    /** The press takes a measure back. Nothing is being chosen, so the dialog
     *  may not say "mit dieser Auswahl". */
    drop: {
      title: 'Maßnahme abwählen',
      body: (n: number) =>
        n === 1
          ? 'Mit dieser Abwahl entfällt die folgende Maßnahme:'
          : 'Mit dieser Abwahl entfallen die folgenden Maßnahmen:',
      confirm: 'Trotzdem abwählen',
    },
    /**
     * A pick whose price was fixed when it was made, and which the catalogue
     * charges differently today. Taking it back gives the fixed price up: a
     * later pick is priced afresh.
     */
    priceLost:
      'Der Preis einer dieser Maßnahmen gilt nur, solange sie gewählt bleibt — wird sie abgewählt, wird eine erneute Auswahl neu berechnet.',
    priceNow: 'Heute',
  },

  /**
   * A press that would change a pick whose price is held — refused rather than
   * confirmed.
   *
   * Says that it cannot be done and who undoes that, and nothing about money:
   * what stands behind the hold is a figure agreed off the catalogue, which is
   * a conversation for the Berater and not a line in a dialog.
   */
  locked: {
    title: 'Diese Auswahl ist gesperrt',
    body: (n: number) =>
      n === 1
        ? 'Die folgende Maßnahme lässt sich gerade nicht abwählen oder ersetzen:'
        : 'Die folgenden Maßnahmen lassen sich gerade nicht abwählen oder ersetzen:',
    hint: 'Möchtest du etwas daran ändern, melde dich kurz bei deinem Berater — er gibt die Auswahl für dich frei.',
    confirm: 'Verstanden',
    /** Said where the refusal arrives as an answer rather than as this dialog:
     *  the press was allowed on screen and the save came back refused. */
    serverError:
      'Diese Maßnahme ist gesperrt und lässt sich gerade nicht ändern. Dein Berater gibt sie für dich frei.',
  },

  menu: {
    open: 'Navigation öffnen',
    close: 'Navigation schließen',
    ariaLabel: 'Navigation',
  },
  /** A question answered further up that this place may depart from. `source`
   *  names where the answer comes from — "Geschoss · Erdgeschoss". */
  inheritance: {
    taken: (source: string) => `Übernommen aus ${source}`,
    departed: (source: string) => `Abweichend von ${source}`,
    reset: 'Zurücksetzen',
    /** On the question further up, once a place below has departed from its
     *  answer: changing it here leaves those places as they are. */
    departures: (n: number) =>
      n === 1 ? '1 Stelle weicht ab' : `${n} Stellen weichen ab`,
    applyToAll: 'Auf alle anwenden',
  },
  emptyTitle: 'Konfigurator wird gerade vorbereitet',
  emptyBody:
    'Aktuell sind noch keine Konfigurations-Optionen verfügbar. Schau in Kürze wieder vorbei.',
  // Public CONFIGURATOR chrome
  enterData: 'Daten angeben',
  backToObject: 'Zum Objekt',

  phoneInputLabel: 'Telefonnummer',
  gateTitle: 'Fast geschafft',
  gateBody:
    'Damit wir dir deine Auswertung zusammen mit einer persönlichen Beratung zur Verfügung stellen können, brauchen wir kurz deinen Namen und deine Telefonnummer.',
  gateSubmit: 'Auswertung anzeigen',

  // Customer CONFIGURATOR
  loadError: 'Konfigurator konnte nicht geladen werden',
  backToProject: 'Zurück zum Projekt',
  preview: COMMON.preview,
  // Alt text when the image itself carries no title.
  detailImageAlt: 'Detail',
  noPreview: 'Keine Vorschau',
  previewAlt: (index: number, total: number) =>
    `Vorschau ${index} von ${total}`,
  continueToNext: (name: string) => `Weiter zu ${name}`,
  continueToOverview: 'Zur Auswertung',
  detailsTooltip: 'Mehr Informationen',
  lockedBannerText:
    'Dieser Konfigurator ist gesperrt und kann derzeit nicht geändert werden.',

  // Customer summary — the CONFIGURATOR's closing screen
  summarySubtitle: 'Alles, was du ausgewählt hast — auf einen Blick.',
  summaryValuePending: '–',
  /** A spec the question compares that this measure does not state. The slot
   *  stays so the cards beside it keep their rows aligned. */
  cardSpecMissing: '–',
  summaryNetSuffix: COST_TERMS.net,
  summaryAllInNote: `Kaufpreis zzgl. rund ${ANCILLARY_COST_RATE * 100} % Kaufnebenkosten und der Sanierungskosten. Verbindlich nach Objektbesichtigung.`,
  summaryOpenInConfigurator: 'Im Konfigurator öffnen',
  summaryBackToConfigurator: 'Konfiguration bearbeiten',
  summaryDownloadPdf: 'Als PDF speichern',
  summaryEmptyTitle: 'Noch nichts ausgewählt',
  summaryEmptyDescription:
    'Sobald du im Konfigurator Optionen auswählst, erscheinen sie hier — Raum für Raum, mit Preisen.',

  // Option badges the customer sees; the CMS edits the same flags.
  fundingEligible: 'Förderfähig',
  lockedDisabled: CONFIGURATOR_STATUS_LABELS.LOCKED,
  lockedRequired: 'Pflicht',
  properties: TERMS.characteristicPlural,
  surcharge: 'Aufpreis',
  colorsAndVariants: 'Farben & Varianten',

  // Public property-input step (generic CONFIGURATOR without a fixed project).
  // Field labels (Wohnfläche, Anzahl Zimmer, Kaufpreis etc.) come from the
  // canonical field registry via useField() — see CLAUDE.md. Only CONFIGURATOR-
  // specific strings live here.
  /** Heads the dialog the property data is entered in. Short: it sits in a
   *  dialog's own title bar, beside the close button. */
  inputStepTitle: 'Deine Immobilie',
  inputStepSubtitle:
    'Mit diesen Angaben berechnen wir die Sanierungskosten so genau wie möglich.',
  inputContinue: 'Weiter zum Konfigurator',
  inputInvalid: 'Ungültig',
} as const
